/*
 * Bundle inventory reconciler.
 *
 * Driven by metafields, not hardcoded IDs. Any product can opt-in by setting:
 *   custom.qty_3 -> Product reference (the "bonus A" product, kicks in at qty 3)
 *   custom.qty_4 -> Product reference (the "bonus B" product, kicks in at qty 4)
 *
 * Each main product publishes a registry entry to window.BUNDLE_INVENTORY_REGISTRY
 * via snippets/bundle-inventory-registry.liquid (rendered on the PDP and per
 * cart line). This script walks that registry on every cart change.
 *
 * Rule (the customer's chosen N is the *total* cart slot count for the bundle,
 * not the count of "main" units — bonuses occupy slots inside N):
 *
 *   For N total bundle units in cart, where N = mainQty + bonusAQty + bonusBQty:
 *     groups    = floor(N / 4)
 *     remainder = N % 4
 *     mainQty   = 2*groups + (remainder if remainder < 3 else 2)
 *     bonusA    = groups + (1 if remainder == 3 else 0)
 *     bonusB    = groups
 *
 *   Verification: every full group of 4 = 2 main + 1 A + 1 B (4 line items).
 *
 * Multiple main products can be registered concurrently; each is reconciled
 * independently. A single /cart/update.js call applies all needed deltas.
 */
(function () {
  'use strict';

  function getRegistry() {
    var reg = window.BUNDLE_INVENTORY_REGISTRY;
    if (!reg || typeof reg !== 'object') return null;
    var entries = Object.keys(reg);
    return entries.length > 0 ? reg : null;
  }

  // Given the customer's total bundle slot count N, return the cart breakdown.
  // N includes both the main product and bonus items — they all occupy slots.
  function computeBundleBreakdown(N) {
    var n = parseInt(N, 10);
    if (!n || n < 0) n = 0;
    var groups = Math.floor(n / 4);
    var remainder = n % 4;
    var mainAdd = remainder < 3 ? remainder : 2;
    return {
      main: 2 * groups + mainAdd,
      a: groups + (remainder === 3 ? 1 : 0),
      b: groups,
    };
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    });
  }

  function getVariantQty(cart, variantId) {
    if (!cart || !cart.items) return 0;
    var item = cart.items.find(function (i) {
      return i.variant_id === variantId;
    });
    return item ? item.quantity : 0;
  }

  // Sum all cart line items belonging to a product, regardless of variant.
  // Used to fold any non-canonical variants (e.g. someone adding a different
  // size of the main product) into the bundle total so they get reconciled.
  function getProductQty(cart, productId) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      return item.product_id === productId ? sum + item.quantity : sum;
    }, 0);
  }

  // Collect every variant id that's currently locked by some registered rule.
  // Used by lockBonusRows() to recognize bonus line items in the cart UI.
  function getAllBonusVariantIds() {
    var reg = getRegistry();
    if (!reg) return [];
    var ids = [];
    Object.keys(reg).forEach(function (key) {
      var cfg = reg[key];
      if (cfg.bonusAVariantId) ids.push(String(cfg.bonusAVariantId));
      if (cfg.bonusBVariantId) ids.push(String(cfg.bonusBVariantId));
    });
    return ids;
  }

  var isReconciling = false;
  var reconcilePending = false;

  function reconcile() {
    var reg = getRegistry();
    if (!reg) return Promise.resolve();

    if (isReconciling) {
      reconcilePending = true;
      return Promise.resolve();
    }
    isReconciling = true;

    return fetchCart()
      .then(function (cart) {
        var updates = {};
        var hasChanges = false;

        Object.keys(reg).forEach(function (key) {
          var cfg = reg[key];
          // The customer's "intent" is the total slot count N, which equals
          // every line item belonging to this bundle currently in cart:
          // all main-product variants + the two bonus variants.
          var currentMain = getProductQty(cart, cfg.mainProductId);
          var currentA = getVariantQty(cart, cfg.bonusAVariantId);
          var currentB = getVariantQty(cart, cfg.bonusBVariantId);

          // Special case: customer just deleted the main product line. Bonuses
          // are locked from manual edits, so the only way to reach
          // (main=0 AND bonuses>0) is via main-row deletion → treat that as
          // "clear the entire bundle" rather than re-creating main from the
          // bonus count.
          var target;
          if (currentMain === 0 && (currentA > 0 || currentB > 0)) {
            target = { main: 0, a: 0, b: 0 };
          } else {
            var N = currentMain + currentA + currentB;
            target = computeBundleBreakdown(N);
          }

          if (currentMain !== target.main) {
            // Reconciler can only address one canonical main variant. If the
            // customer added a different variant of the same product, it stays
            // in cart but the canonical variant absorbs the target qty.
            updates[cfg.mainVariantId] = target.main;
            hasChanges = true;
          }
          if (currentA !== target.a) {
            updates[cfg.bonusAVariantId] = target.a;
            hasChanges = true;
          }
          if (currentB !== target.b) {
            updates[cfg.bonusBVariantId] = target.b;
            hasChanges = true;
          }
        });

        if (!hasChanges) return null;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates }),
        }).then(function (res) {
          if (!res.ok) {
            return res.text().then(function (text) {
              throw new Error('cart update failed: ' + text);
            });
          }
          return res.json();
        });
      })
      .then(function (updatedCart) {
        if (!updatedCart) return;
        // Tell cart-items / cart-drawer-items / cart-icon-bubble to re-render
        // from the now-correct cart state. The 'bundle-inventory' source tag
        // prevents this trigger from looping back into reconcile() below.
        // PUB_SUB_EVENTS / publish are shared globals from constants.js / pubsub.js.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-inventory',
            cartData: updatedCart,
          });
        }
        return refreshCartSections();
      })
      .catch(function (err) {
        console.error('[BundleInventory] reconcile error', err);
      })
      .then(function () {
        isReconciling = false;
        lockBonusRows();
        if (reconcilePending) {
          reconcilePending = false;
          return reconcile();
        }
      });
  }

  // After bundle-inventory mutates the cart, the main-cart-items / cart-drawer
  // sections will be re-rendered by the existing CartItems subscriber. We still
  // need to manually refresh the cart icon bubble (totals/count) and the cart
  // footer totals — the theme's CartItems.onCartUpdate intentionally only
  // replaces the items section, not the footer.
  function refreshCartSections() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    var jobs = [];

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      jobs.push(
        fetch(cartUrl + '?section_id=cart-icon-bubble')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newBubble = doc.querySelector('.shopify-section');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          })
          .catch(function () {})
      );
    }

    var mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset && mainFooter.dataset.id) {
      jobs.push(
        fetch(cartUrl + '?section_id=' + mainFooter.dataset.id)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var contents = doc.querySelector('#main-cart-footer .js-contents');
            var target = mainFooter.querySelector('.js-contents');
            if (contents && target) target.innerHTML = contents.innerHTML;
          })
          .catch(function () {})
      );
    }

    return Promise.all(jobs);
  }

  // Lock the quantity controls and remove button on bonus line items so
  // customers can't manually edit or delete them — bundle-inventory owns them.
  function lockBonusRows() {
    var bonusIds = getAllBonusVariantIds();
    if (bonusIds.length === 0) return;

    var inputs = document.querySelectorAll('input[data-quantity-variant-id]');
    inputs.forEach(function (input) {
      if (bonusIds.indexOf(String(input.dataset.quantityVariantId)) === -1) return;
      var row =
        input.closest('tr.cart-item') ||
        input.closest('.cart-item') ||
        input.closest('[id^="CartItem-"]') ||
        input.closest('[id^="CartDrawer-Item-"]');
      if (!row || row.dataset.bundleLocked === '1') return;
      row.dataset.bundleLocked = '1';

      input.setAttribute('readonly', 'readonly');
      input.setAttribute('aria-readonly', 'true');
      input.style.pointerEvents = 'none';
      input.style.opacity = '0.6';

      row.querySelectorAll('quantity-input button').forEach(function (btn) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.4';
      });

      var removeBtn = row.querySelector('cart-remove-button');
      if (removeBtn) removeBtn.style.display = 'none';

      var detailsCell = row.querySelector('.cart-item__details') || row;
      if (!detailsCell.querySelector('.bundle-bonus-note')) {
        var note = document.createElement('div');
        note.className = 'bundle-bonus-note';
        note.style.cssText =
          'font-size: 11px; color: #555; margin-top: 6px; font-style: italic;';
        note.textContent = 'Included with main product (auto-managed)';
        detailsCell.appendChild(note);
      }
    });
  }

  function init() {
    // No registered bundle products on this page → strict no-op. Avoids the
    // mutation observer cost and the defensive /cart.js fetch entirely.
    if (!getRegistry()) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'bundle-inventory') return;
        reconcile();
      });
    }

    // Reapply lock styling whenever cart sections re-render.
    var observer = new MutationObserver(function () {
      lockBonusRows();
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    lockBonusRows();
    // One-time defensive sync on load (cheap no-op if already consistent).
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BundleInventory = {
    reconcile: reconcile,
    lockBonusRows: lockBonusRows,
    computeBundleBreakdown: computeBundleBreakdown,
  };
})();
